<script setup>
import { computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const getters = useStoreGetters();

const eventAttributes = computed(
  () =>
    getters['attributes/getAttributesByModel'].value('event_attribute') || []
);

const attributeOptions = computed(() =>
  eventAttributes.value.map(attr => ({
    id: attr.attribute_key,
    name: attr.attribute_display_name,
  }))
);

const payload = computed({
  get() {
    const raw = props.modelValue?.[0] || {};
    return {
      attribute_key: raw.attribute_key || '',
      value: raw.value ?? '',
    };
  },
  set(next) {
    emit('update:modelValue', [next]);
  },
});

const selectedAttribute = computed(() =>
  eventAttributes.value.find(
    attr => attr.attribute_key === payload.value.attribute_key
  )
);

const displayType = computed(
  () => selectedAttribute.value?.attribute_display_type || 'text'
);

const listOptions = computed(() =>
  (selectedAttribute.value?.attribute_values || []).map(option => ({
    id: option,
    name: option,
  }))
);

const selectedAttributeModel = computed({
  get() {
    if (!payload.value.attribute_key) return null;
    return (
      attributeOptions.value.find(
        option => option.id === payload.value.attribute_key
      ) || null
    );
  },
  set(option) {
    payload.value = {
      attribute_key: option?.id || '',
      value: '',
    };
  },
});

const selectedListValue = computed({
  get() {
    if (!payload.value.value) return null;
    return (
      listOptions.value.find(option => option.id === payload.value.value) ||
      null
    );
  },
  set(option) {
    payload.value = {
      ...payload.value,
      value: option?.id || '',
    };
  },
});

const textValue = computed({
  get() {
    return payload.value.value ?? '';
  },
  set(value) {
    payload.value = {
      ...payload.value,
      value,
    };
  },
});

const checkboxValue = computed({
  get() {
    return payload.value.value === true || payload.value.value === 'true';
  },
  set(value) {
    payload.value = {
      ...payload.value,
      value,
    };
  },
});
</script>

<template>
  <div class="flex flex-col gap-2 w-full">
    <SingleSelect
      v-model="selectedAttributeModel"
      :options="attributeOptions"
      class="w-full"
    />
    <SingleSelect
      v-if="displayType === 'list' && listOptions.length"
      v-model="selectedListValue"
      :options="listOptions"
      class="w-full"
    />
    <div v-else-if="displayType === 'checkbox'" class="flex items-center gap-2">
      <Switch v-model="checkboxValue" />
      <span class="text-sm text-n-slate-11">
        {{
          checkboxValue
            ? $t('FILTER.ATTRIBUTE_LABELS.TRUE')
            : $t('FILTER.ATTRIBUTE_LABELS.FALSE')
        }}
      </span>
    </div>
    <NextInput
      v-else-if="displayType === 'number' || displayType === 'personnel'"
      v-model="textValue"
      type="number"
      size="sm"
      class="w-full"
      :placeholder="
        $t('AUTOMATION.ACTION.EVENT_CUSTOM_ATTRIBUTE_VALUE_PLACEHOLDER')
      "
    />
    <NextInput
      v-else-if="displayType === 'date'"
      v-model="textValue"
      type="date"
      size="sm"
      class="w-full"
    />
    <NextInput
      v-else-if="displayType === 'time'"
      v-model="textValue"
      type="time"
      size="sm"
      class="w-full"
    />
    <NextInput
      v-else-if="selectedAttribute"
      v-model="textValue"
      size="sm"
      class="w-full"
      :placeholder="
        $t('AUTOMATION.ACTION.EVENT_CUSTOM_ATTRIBUTE_VALUE_PLACEHOLDER')
      "
    />
  </div>
</template>
